import React from 'react';
import PropTypes from 'prop-types';
import { Collapse, Icon, TextArea } from '@blueprintjs/core';

// Building blocks for the New/Edit forms inside FormDrawer (components/FormDrawer).
// Styles: styles/form-drawer.scss (.fb-*).
//
//   <FormSection title="Who and where" hint="The client and the address you'll visit.">…</FormSection>
//   <Choice label="Priority" value={p} onChange={setP} options={[{ value: 'low', label: 'Low' }, …]} />
//   <FieldError>{errors.client}</FieldError>
//   <MoreOptions count={3} open={hasValues}>…rarely used fields…</MoreOptions>
//   <GrowingTextArea id="notes" value={notes} onChange={…} />

// A titled group of fields. `hint` says what the group is for, in plain words.
export const FormSection = ({ title, hint, aside, children }) => (
  <section className="fb-section">
    {(title || aside) && (
      <header className="fb-section__head">
        <div>
          {title && <h3 className="fb-section__title">{title}</h3>}
          {hint && <p className="fb-section__hint">{hint}</p>}
        </div>
        {aside && <div className="fb-section__aside">{aside}</div>}
      </header>
    )}
    <div className="fb-section__body">{children}</div>
  </section>
);

FormSection.propTypes = {
  title: PropTypes.node,
  hint: PropTypes.node,
  aside: PropTypes.node,
  children: PropTypes.node,
};

// A problem with one field, shown under it (instead of a toast on save).
export const FieldError = ({ children, id }) =>
  children ? (
    <p className="fb-error" id={id} role="alert">
      <Icon icon="error" iconSize={12} />
      {children}
    </p>
  ) : null;

FieldError.propTypes = { children: PropTypes.node, id: PropTypes.string };

// Few options (priority, terms, status…): all visible, one click to pick.
// `value` / `onChange(value)`; options are { value, label, hint?, tone? }.
// `tone` (green, red, amber, grey) colours an option when it's picked, for
// values with a meaning of their own (Approved, Rejected).
export const Choice = ({ label, value, onChange, options, required, name, disabled, error }) => (
  <div className={`fb-choice${error ? ' has-error' : ''}`} role="radiogroup" aria-label={typeof label === 'string' ? label : name}>
    {label && (
      <span className="fb-label">
        {label}
        {required && <span className="fb-required" aria-hidden="true"> *</span>}
      </span>
    )}
    <div className="fb-choice__options">
      {options.map((option) => {
        const selected = String(option.value) === String(value);
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            className={`fb-choice__option${selected ? ' is-selected' : ''}${option.tone ? ` is-tone-${option.tone}` : ''}`}
            onClick={() => onChange(option.value)}
            title={option.hint}
          >
            {option.label}
          </button>
        );
      })}
    </div>
    <FieldError>{error}</FieldError>
  </div>
);

Choice.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.any, label: PropTypes.node, hint: PropTypes.string, tone: PropTypes.string })).isRequired,
  required: PropTypes.bool,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.node,
};

// Rarely used fields, folded away. Opens by itself when `open` (e.g. some of
// them already have values, or one has an error).
export class MoreOptions extends React.Component {
  state = { open: Boolean(this.props.open) };

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open && !this.state.open) {
      this.setState({ open: true }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  render() {
    const { label = 'More options', count, children } = this.props;
    const { open } = this.state;
    return (
      <div className={`fb-more${open ? ' is-open' : ''}`}>
        <button
          type="button"
          className="fb-more__toggle"
          aria-expanded={open}
          onClick={() => this.setState({ open: !open })}
        >
          <Icon icon={open ? 'chevron-down' : 'chevron-right'} iconSize={14} />
          {label}
          {count ? <span className="fb-more__count">{count}</span> : null}
        </button>
        <Collapse isOpen={open} keepChildrenMounted={true}>
          <div className="fb-more__body">{children}</div>
        </Collapse>
      </div>
    );
  }
}

MoreOptions.propTypes = {
  label: PropTypes.node,
  count: PropTypes.number,
  open: PropTypes.bool,
  children: PropTypes.node,
};

// A client's do-not-service and alert flags, as slim notes under the customer
// field: a short tag, then the reason.
export const ClientFlags = ({ client }) => {
  const attributes = client && typeof client === 'object' && client.attributes;
  if (!attributes || !(attributes.do_not_service || attributes.is_on_alert)) return null;
  return (
    <div className="fb-flags">
      {attributes.do_not_service && (
        <p className="fb-flag is-danger" role="alert">
          <Icon icon="disable" iconSize={13} />
          <strong>Do not service</strong>
          {attributes.do_not_service_reason && <span>{attributes.do_not_service_reason}</span>}
        </p>
      )}
      {attributes.is_on_alert && (
        <p className="fb-flag is-warning" role="alert">
          <Icon icon="warning-sign" iconSize={13} />
          <strong>Alert</strong>
          {attributes.on_alert_reason && <span>{attributes.on_alert_reason}</span>}
        </p>
      )}
    </div>
  );
};

ClientFlags.propTypes = { client: PropTypes.any };

// An on/off setting: a label (and an optional line on what it does) with a
// switch. `checked` / `onChange(checked)`.
export const Toggle = ({ label, hint, checked, onChange, disabled, id }) => {
  const labelId = id ? `${id}-label` : undefined;
  return (
    <div className={`fb-toggle${disabled ? ' is-disabled' : ''}`}>
      <span className="fb-toggle__text">
        <span className="fb-toggle__label" id={labelId}>{label}</span>
        {hint && <span className="fb-toggle__hint">{hint}</span>}
      </span>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={Boolean(checked)}
        aria-labelledby={labelId}
        aria-label={labelId ? undefined : (typeof label === 'string' ? label : undefined)}
        disabled={disabled}
        className={`fb-toggle__switch${checked ? ' is-on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="fb-toggle__knob" aria-hidden="true" />
      </button>
    </div>
  );
};

Toggle.propTypes = {
  label: PropTypes.node.isRequired,
  hint: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

// A text box that fits its text: it grows as you type and shrinks back when
// text is removed (never below `rows`). Use it instead of Blueprint's
// `growVertically`, which sets the height to the text's height without the
// border, so in these forms the box lost 2px on every keystroke.
export class GrowingTextArea extends React.Component {
  textarea = null;

  componentDidMount() {
    this.fit();
  }

  componentDidUpdate(prevProps) {
    // The value can also change from outside, e.g. the form being cleared.
    if (prevProps.value !== this.props.value) {
      this.fit();
    }
  }

  fit() {
    const el = this.textarea;
    if (!el) return;
    el.style.height = 'auto';
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + border}px`;
  }

  handleRef = (el) => {
    this.textarea = el;
    const { inputRef } = this.props;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) inputRef.current = el;
  }

  handleChange = (event) => {
    this.fit();
    if (this.props.onChange) this.props.onChange(event);
  }

  render() {
    const { inputRef, onChange, rows, ...rest } = this.props;
    return <TextArea {...rest} rows={rows} inputRef={this.handleRef} onChange={this.handleChange} />;
  }
}

GrowingTextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  rows: PropTypes.number,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

GrowingTextArea.defaultProps = {
  rows: 3,
};
